import React, { useEffect, useState } from 'react';
import { MaiDrawer } from './MaiDrawer';
import { fetchMeetingPrep, MeetingPrep } from '../services/MendsVisionService';
import { readApiError } from '../lib/approvalContent';
import { friendlyApiMessage } from '../lib/approvalLabels';

const TOPIC_LABEL: Record<string, string> = {
  perfil: 'Perfil',
  interesse: 'Interesse',
  objecao: 'Objeção',
  orcamento: 'Orçamento',
  prazo: 'Prazo',
  decisor: 'Quem decide',
  compromisso: 'Compromisso',
  historico: 'Histórico',
  outro: 'Ponto',
};

const SOURCE_LABEL: Record<string, string> = {
  conversa: 'da conversa',
  memoria: 'da memória do cliente',
  oportunidade: 'da oportunidade',
  agenda: 'do evento',
};

export type MeetingPrepTarget = {
  eventId?: number | null;
  contactId?: number | null;
  title?: string | null;
};

/**
 * "Preparar reunião": o que saber antes de falar com o cliente. Cada ponto mostra o trecho que o sustenta;
 * o que a MENDS não sabe aparece como "a descobrir", nunca preenchido por palpite.
 */
export const MeetingPrepPanel: React.FC<{
  sectorId: number;
  target: MeetingPrepTarget | null;
  onClose: () => void;
  onOpenChat?: (contactId: number) => void;
}> = ({ sectorId, target, onClose, onOpenChat }) => {
  const [data, setData] = useState<MeetingPrep | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!target || !sectorId) return undefined;
    let alive = true;
    setData(null);
    setError(null);
    setLoading(true);
    fetchMeetingPrep(sectorId, { eventId: target.eventId, contactId: target.contactId })
      .then((res) => { if (alive) setData(res); })
      .catch((e) => {
        if (alive) setError(friendlyApiMessage(readApiError(e).message, 'Não consegui preparar a reunião agora.'));
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [sectorId, target]);

  const subtitle = data?.contactName
    ? `${data.contactName}${data.eventTitle ? ` · ${data.eventTitle}` : ''}`
    : target?.title || null;

  return (
    <MaiDrawer
      open={!!target}
      title="Preparar reunião"
      subtitle={subtitle}
      onClose={onClose}
      footer={data?.contactId && onOpenChat ? (
        <button type="button" className="mai-btn mai-btn-soft" onClick={() => onOpenChat(data.contactId as number)}>
          Abrir conversa
        </button>
      ) : null}
    >
      {loading && <p className="mai-card-detail" role="status">Lendo a conversa, a memória e a oportunidade…</p>}
      {error && <p className="mai-card-detail mai-text-error" role="alert">{error}</p>}
      {data && !data.ok && <p className="mai-card-detail" role="status">{data.message}</p>}
      {data?.ok && (
        <div className="mai-prep">
          <p className="mai-prep-headline">{data.headline}</p>
          {data.points.length > 0 && (
            <section className="mai-prep-section" aria-label="O que saber">
              <h3 className="mai-section-label">O que saber</h3>
              <ul className="mai-prep-points">
                {data.points.map((p, i) => (
                  <li key={`${p.topic}-${i}`}>
                    <span className="mai-pill">{TOPIC_LABEL[p.topic] || 'Ponto'}</span>
                    <span className="mai-prep-text">{p.text}</span>
                    <span className="mai-prep-evidence">
                      “{p.evidence}” <em>{SOURCE_LABEL[p.source] || ''}</em>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {data.unknowns.length > 0 && (
            <section className="mai-prep-section" aria-label="A descobrir">
              <h3 className="mai-section-label">A descobrir na reunião</h3>
              <ul className="mai-prep-list">
                {data.unknowns.map((u) => <li key={u}>{u}</li>)}
              </ul>
            </section>
          )}
          {data.questions.length > 0 && (
            <section className="mai-prep-section" aria-label="Perguntas sugeridas">
              <h3 className="mai-section-label">Perguntas que ajudam</h3>
              <ul className="mai-prep-list">
                {data.questions.map((q) => <li key={q}>{q}</li>)}
              </ul>
            </section>
          )}
          <p className="mai-card-detail mai-prep-foot">
            Montado só com o que está na MENDS. Cada ponto traz o trecho de onde veio.
          </p>
        </div>
      )}
    </MaiDrawer>
  );
};
